import Phaser from 'phaser';
import {
  ReactToPhaserListeners,
  NotifyReact,
  PhaserListenForReact,
  NotifyReactEvents,
  RemoveListener,
} from 'Phaser/EventBus2';
import { colors } from 'Phaser/config';
import ExerciseBase, { IObjective } from './ExerciseBase';
import Cursor, { StaffNote, StartTiming } from 'Phaser/GameObjects/CursorLegacy';
import { Wipe } from 'Phaser/Rendering/Transitions';
import TimeSignature from 'Models/TimeSignature';
import IPlayable from 'Phaser/GameObjects/Playable';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import { INoteGraphicsDatabase } from './StaffExercise';
import { COLORBLIND_TYPE, ERROR_REC_CLASS } from 'Utils/Constants';
import { getColorByCbType } from 'Components/Colors';

interface TempoObjective extends IObjective {
  numBeats: number;
  tempo: number;
  instructionWaitTime?: number;
}

export interface Config {
  chapterIdx?: number;
  paddingVertical?: number;
  paddingHorizontal?: number;
  cbType?: COLORBLIND_TYPE;
  objectives: TempoObjective[];
}

type BeatState = 'hit' | 'miss' | 'late' | 'idle';

class Beat implements IPlayable {
  isFocused: boolean = false;
  graphic: Phaser.GameObjects.Text;
  state: BeatState = 'idle';
  tweenHandle?: Phaser.Tweens.Tween;
  scene: Phaser.Scene;
  resetColorTimer?: Phaser.Time.TimerEvent;
  tempo: number;
  cbType?: COLORBLIND_TYPE;

  constructor(
    graphic: Phaser.GameObjects.Text,
    scene: Phaser.Scene,
    tempo: number,
    cbType?: COLORBLIND_TYPE,
  ) {
    this.cbType = cbType;
    this.graphic = graphic;
    this.scene = scene;
    this.tempo = tempo;
  }

  getHaloOffset() {
    return 0;
  }

  getState() {
    switch (this.state) {
      case 'hit':
        return ProgressState.Ended;
      case 'late':
        return ProgressState.LateStart;
      case 'miss':
        return ProgressState.Missed;
    }
  }

  update(_: number) {}

  setColor(_color: number | string) {}
  pulse() {}

  setTimingState(state: ProgressState) {
    switch (state) {
      case ProgressState.TimelyStart: {
        this.state = 'hit';
        this.graphic.setColor(getColorByCbType(ERROR_REC_CLASS.CORRECT, this.cbType ?? null));
        break;
      }
      case ProgressState.LateStart:
        this.state = 'late';
        this.graphic.setColor(getColorByCbType(ERROR_REC_CLASS.SEMI_ERROR, this.cbType ?? null));
        break;

      case ProgressState.Missed:
        this.graphic.setColor(getColorByCbType(ERROR_REC_CLASS.ERROR, this.cbType ?? null));
        this.state = 'miss';
        break;
      default:
        break;
    }
  }

  getPosition() {
    return {
      x: this.graphic.x,
      y: this.graphic.y,
    };
  }

  reset(forceColor: boolean = false) {
    this.isFocused = false;
    this.tweenHandle?.destroy();
    this.resetColorTimer?.remove();
    if (forceColor) this.graphic.setColor(colors.beatNumberFillQuestion);
    else {
      this.resetColorTimer = this.scene.time.delayedCall(
        2500,
        () => this.graphic.setColor(colors.beatNumberFillQuestion),
        [],
        this,
      );
    }
  }

  setFocus(focus: boolean): void {
    this.resetColorTimer?.remove();
    this.isFocused = focus;
    if (!focus) return this.tweenHandle?.destroy();
    this.tweenHandle = this.scene.tweens.add({
      targets: this.graphic,
      scale: 1.3,
      x: this.graphic.x - this.graphic.width * 0.15,
      y: this.graphic.y - this.graphic.height * 0.15,
      ease: Phaser.Math.Easing.Quadratic.InOut,
      yoyo: true,
      duration: 150,
    });
  }

  shake(): void {
    this.tweenHandle?.remove();
    const target = this.graphic;
    const pos = target.x;
    const dpr = 1;
    const duration = Math.min((60 / this.tempo) * 1000, 200);
    this.tweenHandle = this.scene.tweens.add({
      targets: { value: 0 },
      value: Math.PI * 2,
      duration,
      ease: Phaser.Math.Easing.Sine.In,
      repeat: 1,
      onUpdate: (_tween, _target, _key, current) => {
        target.setX(pos + Math.sin(current) * 10 * dpr);
      },
    });
  }

  destroy() {
    this.isFocused = false;
    this.tweenHandle?.destroy();
    this.resetColorTimer?.remove();
    this.graphic?.destroy();
  }
}

export class ExerciseTempoPractice
  extends ExerciseBase<TempoObjective, Config>
  implements INoteGraphicsDatabase
{
  table: Map<string, IPlayable>;
  nextEarly: boolean = false;
  config?: Config;
  cursor?: Cursor;
  playAreaSize?: { width: number; height: number; x: number; y: number };
  beatGraphics: Phaser.GameObjects.Text[];
  currentBeat: number;
  beatState: BeatState[];
  beats: Beat[];
  beatTweenHandle: Phaser.Tweens.Tween[] = [];
  lateTimer?: Phaser.Time.TimerEvent;
  missTimer?: Phaser.Time.TimerEvent;
  completionTimer?: Phaser.Time.TimerEvent;
  hitBuffer: boolean = false;
  hitBufferResetTimer?: Phaser.Time.TimerEvent;
  // running is for keeping track of the cursor's movement
  running: boolean = false;
  // started is to determine whether the exercise has started and is ready for input or not
  started: boolean = false;
  history: BeatState[] = [];
  cursorTweenHandle?: Phaser.Tweens.Tween;
  metronomeHandle?: Phaser.Time.TimerEvent;
  startTimerHandle?: Phaser.Time.TimerEvent;
  shakeHandle?: Phaser.Tweens.Tween;

  constructor() {
    super('ExerciseTempoPractice');
    this.beatGraphics = [];
    this.beatState = [];
    this.beats = [];
    this.currentBeat = -1;
    this.running = false;
    this.table = new Map();
  }

  getNoteById(id: string): IPlayable | null {
    return this.table.get(id) ?? null;
  }

  getNotePosition(id: string) {
    return this.getNoteById(id)?.getPosition().x ?? null;
  }

  getPhraseEndPosition() {
    return 0;
  }

  pendingStart: boolean = false;

  init(config: Config) {
    this.config = config;
    this.currentObjective = -1;
    this.pendingStart = false;
    this.setupBackground();
    super.init(config);
  }

  skipLoadTransition() {
    //(this.cameras.main.getPostPipeline('Wipe') as Wipe).progress = 1;
    setTimeout(() => NotifyReactEvents.ShowExercise(undefined), 50);
  }

  transition(skip?: boolean, last?: boolean) {
    this.wipe(
      true,
      () => {
        this.clear();
        super.transition(skip, last);
      },
      this,
    );
  }

  wipe(reverse?: boolean, onComplete?: () => void, ctx?: object): void {
    const targets = this.cameras.main.getPostPipeline('Wipe') as Wipe;
    this.tweens.add({
      targets,
      progress: reverse ? 0.0 : 1.0,
      duration: reverse ? 1000 : 1000,
      onComplete,
      callbackScope: ctx,
    });
    console.log('wipe: wipe');
    if (!reverse) {
      setTimeout(() => NotifyReactEvents.ShowExercise(undefined), 50);
    }
  }

  updateObjective({ chapter }: { chapter: number }): void {
    if (!this.config || chapter !== this.config.chapterIdx) {
      return;
    }

    const nextObjective = (this.currentObjective ?? -1) + 1;
    console.log('wipe: what', nextObjective);

    // Guard: check bounds BEFORE incrementing to avoid leaving state invalid
    if (nextObjective >= this.config.objectives.length) {
      console.warn(
        'ExerciseTempoPractice.updateObjective: next objective would be out of bounds, ignoring',
        {
          currentObjective: this.currentObjective,
          nextObjective,
          objectivesLength: this.config.objectives.length,
        },
      );
      return;
    }

    const objective = this.config.objectives[nextObjective];
    if (!objective) {
      console.warn(
        'ExerciseTempoPractice.updateObjective: objective is undefined at index',
        nextObjective,
      );
      return;
    }

    // Only update currentObjective after validation passes
    this.currentObjective = nextObjective;

    super.showInstruction(
      objective.text ?? 'Use any key to tap out the beats in sync with the tempo.',
    );
    this.wipe();
    this.instantiateObjects();

    // If startExercise was called before updateObjective (init/create timing),
    // it deferred. Now that objectives and beats are set up, run it.
    if (this.pendingStart) {
      this.startExercise();
    }
  }

  onObjectiveComplete(): void {
    //this.transition(true, this.clear, this);
  }

  clear() {
    this.beatGraphics.forEach((g) => g.destroy());
    this.beatState.clear();
    this.history.clear();
    this.beatGraphics.clear();
    this.beats.clear();
    this.beats = [];
    this.beatGraphics = [];
    this.cursor?.destroy();
    this.started = false;
  }

  instantiateObjects() {
    console.log('instantiating');
    // Pixel ratio for retina displays. Without this, the canvas will be blurry on retina displays.
    const dpr = 1;
    const paddingH = (this.config!.paddingHorizontal ?? 50) * dpr;
    const paddingV = (this.config!.paddingVertical ?? 50) * dpr;
    this.playAreaSize = {
      width: this.cameras.main.width - paddingH * 2,
      height: this.cameras.main.height - paddingV * 2,
      x: paddingH,
      y: paddingV,
    };

    // Guard: ensure objective index is valid
    const objectiveIndex = Math.max(this.currentObjective ?? 0, 0);
    if (!this.config || objectiveIndex >= this.config.objectives.length) {
      console.warn('ExerciseTempoPractice.instantiateObjects: objective index out of bounds', {
        objectiveIndex,
        objectivesLength: this.config?.objectives?.length,
      });
      return;
    }

    const objective = this.config.objectives[objectiveIndex];
    if (!objective) {
      console.warn(
        'ExerciseTempoPractice.instantiateObjects: objective is undefined at index',
        objectiveIndex,
      );
      return;
    }

    const { tempo, numBeats } = objective;
    const yMid = this.playAreaSize.height / 2;
    const beatWidth = this.playAreaSize.width / (numBeats + 1);
    const offset = this.playAreaSize.x;
    for (let i = 0; i < numBeats; i++) {
      const graphic = this.add.text(
        (i + 1) * beatWidth + offset + 3 * dpr,
        yMid - 30 * dpr,
        (i + 1).toString(),
        {
          fontFamily: 'Lato',
          fontStyle: 'bold',
          fontSize: 85 * dpr,
          color: colors.beatNumberFillQuestion,
        },
      );
      graphic.setX(graphic.x - graphic.displayWidth / 2);
      this.beatGraphics.push(graphic);
      this.beats.push(new Beat(graphic, this, tempo, this.config?.cbType));
    }
    const beatDuration = 60000 / tempo;
    const cursorData: StaffNote[] = this.beats.map((beat) => {
      return {
        position: { x: beat.graphic.x, y: beat.graphic.y },
        durationInMs: beatDuration,
        durationRelative: 0.25,
        playable: beat,
      };
    });
    console.debug(cursorData);
    const endPos = {
      x: this.playAreaSize.width + this.playAreaSize.x,
      y: yMid - 30 * dpr,
    };
    cursorData.push({ position: endPos, durationInMs: 0, durationRelative: 0 });
    this.cursor = new Cursor(
      this,
      offset + beatWidth - this.beatGraphics[0].displayWidth / 2,
      yMid - 150 * dpr,
      45 * dpr,
      300 * dpr,
      20 * dpr,
      new TimeSignature(4, 4),
      tempo,
      cursorData,
    );
  }

  midiOn(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- MIDI callback payload
    _?: any,
  ): void {
    if (this.started) {
      this.onPressKey();
    }
  }

  startExercise() {
    // Guard: if updateObjective hasn't run yet (create() hasn't fired),
    // defer the start. updateObjective will call startExercise when ready.
    if (this.currentObjective === undefined || this.beats?.length === 0) {
      this.pendingStart = true;
      return;
    }
    this.pendingStart = false;
    this.started = true;
    this.cursor?.reset();
    this.running = false;
    this.history = [];
    if (this.beats) {
      this.beats.forEach((beat) => beat.reset());
    } else {
      this.instantiateObjects();
    }
    this.currentBeat = -1;
    this.hitBuffer = false;
    this.cursor!.waitForInput();
  }

  createGapVisualObject(index: number) {
    const dpr = 1;
    const leftBeat = this.beatGraphics[index];
    const rightBeat = this.beatGraphics[index + 1];
    const padding = 10 * dpr;
    const x = leftBeat.x + leftBeat.displayWidth + padding;
    const y = leftBeat.y + leftBeat.getBounds().height / 2;
    const x2 = rightBeat.x - padding;
    const width = x2 - x;
    const height = 2 * dpr;
    const lines = [];
    lines.push(this.add.rectangle(x + height / 2, y, height, 20 * dpr, 0));
    lines.push(this.add.rectangle(x + width / 2, y, width, height, 0x00));
    lines.push(this.add.rectangle(x2 - height / 2, y, height, 20 * dpr, 0));
    return lines;
  }

  onPressKey() {
    let timing: StartTiming;
    if (this.running === false) {
      [timing] = this.cursor!.tryStart(this.onExerciseEnd, this);
      if (timing === 'early') {
        this.showToast('Too early');
        this.beats[0]?.shake();
      } else if (timing === 'late') {
        this.showToast('Too late');
        this.beats[0]?.shake();
      } else {
        this.beatGraphics.forEach((graphic) => graphic.setColor(colors.beatNumberFillQuestion));
        this.running = true;
        this.cursor?.onHoldNote();
      }
    } else {
      timing = this.cursor!.checkTiming();
      this.cursor?.onHoldNote();
    }
    /*if (this.currentBeat === -1) {
      this.nextBeat();
    }
    switch (timing) {
      case "early":
        this.setBeatState("late");
        break;
      case "late":
        this.setBeatState("late");
        break;
      case "perfect":
        this.setBeatState("hit");
        break;
    }*/
  }

  stop() {
    this.scene.stop();
  }

  update(time: number) {
    this.cursor?.update(time);
  }

  setupBackground() {}

  passObjective() {
    this.started = false;
    this.cursor?.stop();
    super.passObjective(this.isLastObjective());
  }

  passExercise(): void {
    super.passObjective(true);
  }

  onExerciseEnd() {
    this.beats.forEach((beat) => console.log('beat state: ', beat.state));
    if (
      this.beats.find(
        (beat) => beat.state === 'miss' || beat.state === 'late' || beat.state === 'idle',
      ) !== undefined
    ) {
      this.running = false;
      this.failExercise();
      return false;
    } else {
      this.running = false;
      this.passObjective();
      return true;
    }
    //EventBus.emit("exercise-ended");
  }

  unload(): void {
    console.log('unloaded tempo');
    this.clear();
    this.cursor = undefined;
    super.unload();
  }

  changeScene() {
    //nextExercise(this.config!.exerciseIndex, this.scene);
  }

  pauseExercise() {
    this.cursor?.pause();
    super.pauseExercise();
  }

  resume() {
    this.cursor?.resume();
    super.resume();
  }

  restart(): void {
    this.history.clear();
    this.startExercise();
  }

  create() {
    try {
      // This event must be emitted before using any of the integrated phaser modules such as tweens, physics, etc.
      this.cameras.main.setPostPipeline('Wipe');
      NotifyReactEvents.CurrentSceneReady(this);
      // Blanket-clear UpdateObjective before re-registering with collectWaiting.
      // Safe because create() runs synchronously during scene startup.
      RemoveListener('UpdateObjective');
      // Register with collectWaiting=true so any UpdateObjective events
      // dispatched between init() and create() are drained when this runs.
      const updateObjCallback = (data: { chapter: number }) => {
        this.updateObjective(data);
      };
      this.boundCallbacks.set('UpdateObjective', updateObjCallback as (...args: never[]) => void);
      ReactToPhaserListeners.UpdateObjective(updateObjCallback, this, undefined, true);
    } catch (err) {
      console.error('ExerciseTempoPractice.create failed', err);
    }
  }
}
